import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import {
  PropertyGrid,
  Input,
  Select,
  Button,
  Tabs,
  ResponsiveDialog,
  useViewport,
} from '@raffle/ui';
import { usePropertiesStore } from '../stores/propertiesStore';
import { toPropertyData } from '../lib/propertyAdapter';

type SortOption = 'newest' | 'price_low' | 'price_high' | 'ending_soon' | 'popular';

export const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useViewport();

  const { properties, isLoading, error, fetchProperties } = usePropertiesStore();
  
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'closed' | 'drawn'>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000000000]);
  const [showFilters, setShowFilters] = useState(false);

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'ending_soon', label: 'Ending Soon' },
    { value: 'popular', label: 'Most Popular' },
  ];

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'closed', label: 'Closed' },
    { id: 'drawn', label: 'Drawn' },
  ];

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void fetchProperties({
        page: 1,
        status: status === 'all' ? 'all' : status,
        search: search.trim() || undefined,
      });
    }, 300);

    return () => window.clearTimeout(handle);
  }, [fetchProperties, search, status]);

  const filteredProperties = useMemo(() => {
    let filtered = properties.map(toPropertyData);

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(searchLower) ||
          p.location.toLowerCase().includes(searchLower)
      );
    }

    // Filter by status (server already filters; keep as a safety net)
    if (status !== 'all') filtered = filtered.filter((p) => p.status === status);

    // Filter by price range
    filtered = filtered.filter(
      (p) => p.ticketPrice >= priceRange[0] && p.ticketPrice <= priceRange[1]
    );

    // Sort
    switch (sortBy) {
      case 'price_low':
        filtered.sort((a, b) => a.ticketPrice - b.ticketPrice);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.ticketPrice - a.ticketPrice);
        break;
      case 'ending_soon':
        filtered.sort((a, b) => new Date(a.drawDate).getTime() - new Date(b.drawDate).getTime());
        break;
      case 'popular':
        filtered.sort((a, b) => b.ticketsSold - a.ticketsSold);
        break;
      default:
        // newest - sort by draw date descending as a reasonable default
        filtered.sort((a, b) => new Date(b.drawDate).getTime() - new Date(a.drawDate).getTime());
    }

    return filtered;
  }, [properties, priceRange, search, sortBy, status]);

  const FilterContent = () => (
    <div className="p-6 space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-3">
          Price Range (per ticket)
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            placeholder="Min"
            value={priceRange[0] || ''}
            onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
            inputSize="sm"
          />
          <Input
            type="number"
            placeholder="Max"
            value={priceRange[1] === 1000000000 ? '' : priceRange[1]}
            onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 1000000000])}
            inputSize="sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-secondary mb-3">
          Sort By
        </label>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          options={sortOptions}
          inputSize="sm"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          variant="ghost"
          className="flex-1"
          onClick={() => {
            setPriceRange([0, 1000000000]);
            setSortBy('newest');
          }}
        >
          Reset
        </Button>
        <Button
          variant="gold"
          className="flex-1"
          onClick={() => setShowFilters(false)}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );

  return (
    <div className="page-container">
      {/* Header */}
      <section className="bg-surface-1 border-b border-white/5">
        <div className="content-wrapper py-8 md:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Property Raffles
            </h1>
            <p className="text-text-secondary max-w-2xl">
              Browse all available properties and enter for your chance to win. 
              Each ticket increases your odds.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="sticky top-0 md:top-20 z-sticky-bar bg-surface-0/95 backdrop-blur-sm border-b border-white/5">
        <div className="content-wrapper py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search properties..."
                inputSize="sm"
                leftIcon={<Search className="w-4 h-4" />}
                rightIcon={
                  search && (
                    <button onClick={() => setSearch('')}>
                      <X className="w-4 h-4" />
                    </button>
                  )
                }
              />
            </div>

            {/* Status Tabs (Desktop) */}
            {!isMobile && (
              <Tabs
                items={tabs}
                activeTab={status}
                onTabChange={(id) => setStatus(id as typeof status)}
              />
            )}

            {/* Filter Button */}
            <Button
              variant="ghost"
              onClick={() => setShowFilters(true)}
              className="md:ml-auto"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Status Tabs (Mobile) */}
          {isMobile && (
            <div className="mt-4 -mx-4 px-4 overflow-x-auto hide-scrollbar">
              <Tabs
                items={tabs}
                activeTab={status}
                onTabChange={(id) => setStatus(id as typeof status)}
                className="min-w-max"
              />
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="section-spacing">
        <div className="content-wrapper">
          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-text-muted">
              {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'} found
            </p>
            {!isMobile && (
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                options={sortOptions}
                inputSize="sm"
                className="w-48"
              />
            )}
          </div>

          {/* Property Grid */}
          {error ? (
            <div className="p-6 rounded-vault-lg bg-surface-2/40 border border-white/5 text-text-secondary">
              Unable to load properties: {error}
            </div>
          ) : isLoading ? (
            <div className="p-6 rounded-vault-lg bg-surface-2/40 border border-white/5 text-text-secondary">
              Loading properties…
            </div>
          ) : filteredProperties.length > 0 ? (
            <PropertyGrid
              properties={filteredProperties}
              onPropertyClick={(property) => navigate(`/properties/${property.id}`)}
            />
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-text-muted" />
              </div>
              <h3 className="font-display text-lg font-semibold text-text-primary mb-2">
                No properties found
              </h3>
              <p className="text-text-secondary mb-6">
                Try adjusting your search or filters
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setStatus('all');
                  setPriceRange([0, 1000000000]);
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Filter Dialog */}
      <ResponsiveDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filters"
        modalSize="sm"
      >
        <FilterContent />
      </ResponsiveDialog>
    </div>
  );
};
